"use client";

import styles from "./Dropdown.module.scss";
import classNames from "classnames";
import { useEventListener, useOnClickOutside } from "usehooks-ts";
import { DropdownCheckIcon } from "../icons";
import { createPortal } from "react-dom";
import {
  useImperativeHandle,
  useLayoutEffect,
  createContext,
  useCallback,
  useContext,
  forwardRef,
  useEffect,
  ReactNode,
  useState,
  useRef,
} from "react";

const DropdownContext: any = createContext({
  isOpen: false,
  setIsOpen: () => {},
  triggerRef: { current: null } as React.RefObject<HTMLElement>,
  selected: [] as string[],
  toggleSelect: (_: string) => {},
  isSelected: (_: string) => false,
  resetSelected: () => {},
  selectionMode: "single",
});

const DropdownTrigger = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) => {
  const { setIsOpen, triggerRef }: any = useContext(DropdownContext);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (ref.current) {
      triggerRef.current = ref.current;
    }
  }, []);

  return (
    <div
      ref={ref}
      className={classNames(styles["comp__trigger"], className)}
      onClick={(event) => {
        event.stopPropagation();
        setIsOpen((prev: boolean) => !prev);
      }}
    >
      {children}
    </div>
  );
};

const DropdownContent = ({
  children,
  width = null,
  lockBodyScroll = true,
}: {
  children: ReactNode;
  width?: null | number;
  lockBodyScroll?: boolean;
}) => {
  const { isOpen, triggerRef, setIsOpen }: any = useContext(DropdownContext);

  const [style, setStyle] = useState<React.CSSProperties>({});
  const [shouldRender, setShouldRender] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const offset = 10;

  useOnClickOutside(contentRef, (event) => {
    const triggerEl = triggerRef.current;
    if (triggerEl && triggerEl.contains(event.target as Node)) return;
    setIsOpen(false);
  });

  useLayoutEffect(() => {
    if (!isOpen) {
      setShouldRender(false);
      return;
    }

    const triggerEl = triggerRef.current;
    if (triggerEl) setShouldRender(true);
  }, [isOpen]);

  useLayoutEffect(() => {
    if (!shouldRender) return;

    const triggerEl = triggerRef.current;
    const contentEl = contentRef.current;

    if (triggerEl && contentEl) {
      const triggerRect = triggerEl.getBoundingClientRect();
      const contentRect = contentEl.getBoundingClientRect();
      const contentHeight = contentRect.height;
      const spaceBelow = window.innerHeight - triggerRect.bottom;
      const showAbove = spaceBelow < contentHeight + offset;

      const top = showAbove
        ? triggerRect.top - contentHeight - offset
        : triggerRect.bottom + offset;

      const contentWidth = contentRect.width;
      const triggerLeft = triggerRect.left;
      const triggerRight = triggerRect.right;

      const fitsRight = triggerLeft + contentWidth <= window.innerWidth;
      const fitsLeft = triggerRight - contentWidth >= 0;

      const left = fitsRight
        ? triggerLeft
        : fitsLeft
        ? triggerRight - contentWidth
        : Math.max(0, window.innerWidth - contentWidth);

      setStyle({
        position: "fixed",
        top: `${top}px`,
        left: `${left}px`,
        minWidth: `${triggerRect.width}px`,
        zIndex: 1010,
        maxHeight: "60vh",
        overflowY: "auto",
      });
    }
  }, [shouldRender]);

  useEffect(() => {
    if (!lockBodyScroll) return;
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen, lockBodyScroll]);

  if (!isOpen) return null;

  return createPortal(
    <div
      ref={contentRef}
      className={styles["comp__content"]}
      style={{
        ...style,
        width: width === null ? style.width : width,
        visibility: style.top ? "visible" : "hidden",
      }}
      tabIndex={-1}
      onClick={(e) => e.stopPropagation()} // Zapobiega propagacji eventu do rodzica (modala)
    >
      {children}
    </div>,
    document.body
  );
};

const DropdownItem = ({
  children,
  className,
  id,
  disabled = false,
  selectable = true,
  style = {},
  onClick = () => {},
}: {
  id: string;
  children: ReactNode;
  className?: string;
  disabled?: boolean;
  selectable?: boolean;
  style?: any;
  onClick?: (e: any) => void;
}) => {
  const { toggleSelect, isSelected, selectionMode, setIsOpen }: any =
    useContext(DropdownContext);
  const ref = useRef<HTMLDivElement>(null);

  const handleClick = (e: any) => {
    if (disabled) return;
    e.stopPropagation(); // Zapobiega propagacji eventu do rodzica (modala)
    if (selectable) toggleSelect(id);
    onClick(e);
    if (selectionMode === "single") setIsOpen(false);
  };

  return (
    <div
      ref={ref}
      data-dropdown-item
      style={style}
      onClick={handleClick}
      className={classNames(
        styles["comp__item"],
        className,
        disabled && styles["comp__item--disabled"],
        selectable && isSelected(id) && styles["comp__item--selected"]
      )}
    >
      <div>{children}</div>

      {selectable && isSelected(id) && <DropdownCheckIcon />}
    </div>
  );
};

const Dropdown = forwardRef(
  (
    {
      children,
      selectionMode = "single",
      onSelect,
      defaultValue = [], // Dodajemy defaultValue
    }: {
      children: ReactNode;
      selectionMode?: "single" | "multiple";
      onSelect?: (selected: string[] | string | null) => void;
      defaultValue?: string[] | string; // Typ dla defaultValue
    },
    ref
  ) => {
    const triggerRef = useRef<HTMLElement | null>(null);
    const [isOpen, setIsOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    const [selected, setSelected] = useState<string[]>(
      Array.isArray(defaultValue) ? defaultValue : [defaultValue] // Sprawdzamy, czy defaultValue to tablica
    );

    const toggleSelect = useCallback(
      (id: string) => {
        if (selectionMode === "single") {
          setSelected([id]);
        } else {
          setSelected((prev) =>
            prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
          );
        }
      },
      [selectionMode]
    );

    const isSelected = useCallback(
      (id: string) => selected.includes(id),
      [selected]
    );

    // Notify parent of selection change
    useEffect(() => {
      if (!onSelect) return;
      if (selectionMode === "single") {
        onSelect(selected[0] ?? null);
      } else {
        onSelect(selected);
      }
    }, [selected]);

    useImperativeHandle(ref, () => ({
      reset: () => setSelected([]),
    }));

    useEventListener("keydown", (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setIsOpen(false);
      }
    });

    return (
      <DropdownContext.Provider
        value={{
          isOpen,
          setIsOpen,
          triggerRef,
          activeIndex,
          setActiveIndex,
          selected,
          toggleSelect,
          isSelected,
          selectionMode,
        }}
      >
        {children}
      </DropdownContext.Provider>
    );
  }
);

export { Dropdown, DropdownTrigger, DropdownContent, DropdownItem };
